import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyUpdate as applyServiceWorkerUpdate,
  register
} from '../serviceWorkerRegistration';

export type UpdateStatus = 'idle' | 'installing' | 'ready' | 'activating';

interface UseServiceWorkerUpdateResult {
  status: UpdateStatus;
  applyUpdate: () => void;
}

// Module-level guard so React StrictMode double-invocation in dev (and any
// accidental re-mounts) don't register the service worker twice.
let registered = false;

/**
 * Owns the PWA update lifecycle and exposes simple state for the UI to render
 * a "new version available — restart now" prompt.
 *
 * - `idle`: nothing to do, current version is up to date.
 * - `installing`: a new SW is downloading/installing in the background.
 * - `ready`: a new SW is installed and waiting; user can apply it.
 * - `activating`: SKIP_WAITING posted; awaiting controllerchange to reload.
 */
export function useServiceWorkerUpdate(): UseServiceWorkerUpdateResult {
  const [status, setStatus] = useState<UpdateStatus>('idle');
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (registered) return;
    registered = true;

    register({
      onUpdateInstalling: () => {
        setStatus((prev) => (prev === 'idle' ? 'installing' : prev));
      },
      onUpdateReady: (registration) => {
        registrationRef.current = registration;
        setStatus('ready');
      },
      onControllerChange: () => {
        // The new service worker has taken control. Reload to pick up the
        // fresh assets.
        if (fallbackTimerRef.current) {
          clearTimeout(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
        window.location.reload();
      }
    });

    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, []);

  const applyUpdate = useCallback(() => {
    const registration = registrationRef.current;
    if (!registration || !registration.waiting) {
      // Nothing to apply — fall back to a plain reload so the user is
      // never stuck staring at a non-functional button.
      window.location.reload();
      return;
    }
    setStatus('activating');
    applyServiceWorkerUpdate(registration);

    // Safety net: if `controllerchange` doesn't fire (browser quirk, no
    // clientsClaim, etc.), force a reload after a short delay.
    fallbackTimerRef.current = setTimeout(() => {
      window.location.reload();
    }, 5000);
  }, []);

  return { status, applyUpdate };
}
